import {useContext} from "react";
import {Link, useLocation} from "react-router-dom";
import {useTranslation} from "react-i18next";

// Assets
import {IckonicLogo, IckonicLogoIcon} from "../../Assets/SVGs/Logos";
import IconInformationBubble from "../../Assets/SVGs/Icons/AlertAndInfo/InformationBubble";
import styled from "styled-components";

// Components
import HeaderMenu from "../Menus/HeaderMenu";
import ButtonPillGradient from "../Buttons/ButtonPillGradient";
import Notifications from "./Notifications";
import SearchBar from "./SearchBar";
import AccountDropDown from "../Menus/AccountDropDown";
import ChangelogPopup from "../Popups/ChangelogPopup";

// Context
import {GlobalInterfaceContext} from "../../Context/GlobalInterfaceContext";
import {UserAuthenticationContext} from "../../Context/UserAuthenticationContext";
import {usePopup, PopupType} from "../../Context/PopupContext";

// Constants
import {RoutePaths} from "../../Constants/RoutePaths";

// Helpers
import {CanAccessMemberOnlyContent, HasLifetimeMembership, IsSubscriptionActive} from "../../Helpers/Subscription";
import {IsFreeAccount} from "../../Helpers/Account/Admin";
import IconAlertError from "../../Assets/SVGs/Icons/AlertAndInfo/AlertError";
import IconInfoCircle from "../../Assets/SVGs/Icons/AlertAndInfo/InfoCircle";

const Header = (props: { shouldDisplayNotifyBar: boolean }) => {
    const

        // Context
        {
            isDarkMode,
            mainMenuDesktopMode,
            hasNotificationBar,
            mainMenuOpen,
            toggleMainMenuOpenState
        } = useContext(GlobalInterfaceContext),
        {userData, logOutUser, doesAuthTokenExist} = useContext(UserAuthenticationContext),
        {openPopup} = usePopup(),
        {t} = useTranslation(),
        {shouldDisplayNotifyBar} = props,

        needsRenew = userData?.id &&
        !IsSubscriptionActive(userData?.subscription?.status) &&
        !userData?.subscription?.is_entitled &&   // paused-in-grace keeps access → not "needs renew"
        !HasLifetimeMembership(userData?.subscription?.plan_id) &&
        !IsFreeAccount(userData?.admin_role_id),

        // Misc
        location = useLocation();

    return (
        <header className={`header--global
            ${isDarkMode ? 'is-dark-mode' : 'has-light-mode'}
            ${mainMenuDesktopMode ? 'has-desktop-menu' : 'has-mobile-menu'}
            ${shouldDisplayNotifyBar && hasNotificationBar ? 'has-notification-bar' : 'notification-bar-hidden'}
            ${doesAuthTokenExist() ? 'is-authenticated' : 'is-not-authenticated'}
        `}>
            {location.pathname === '/' ?
                (
                    <div className="header__logo">
                        {IckonicLogoIcon()}
                        {IckonicLogo()}
                    </div>
                )
                :
                (
                    <Link
                        className="header__logo"
                        to={'/'}
                    >
                        {IckonicLogoIcon()}
                        {IckonicLogo()}
                    </Link>
                )
            }

            <div className="header__cover u-full_cover_absolute"/>

            <button
                className={`button--burger ${mainMenuOpen ? 'menu-is-active' : 'menu-inactive'}`}
                onClick={() => toggleMainMenuOpenState(!mainMenuOpen)}
            >
                <span>{t('header-ui-hamburger-button-label')}</span>
            </button>

            <HeaderMenu
                isDarkMode={isDarkMode}
                menuActive={mainMenuOpen}
                hasNotificationBar={shouldDisplayNotifyBar && hasNotificationBar}
                userData={userData}
            />

            {!doesAuthTokenExist() &&
                (
                    <ButtonPillGradient
                        link={RoutePaths.SignUp[0]}
                        text="Sign Up"
                        className=""
                        background="linear-gradient(225deg, rgba(187, 54, 184, 1) 0%, #b760b4 100%)"
                        backgroundHover="#b760b4"
                        color=""
                    />
                )
            }

            {/* Pass Auth props down to the Search component */}
            <SearchBar
                userData={userData}
                authTokenExists={doesAuthTokenExist()}
                className={`${mainMenuDesktopMode ? 'has-desktop-menu' : 'has-mobile-menu'} ${needsRenew ? 'is-not-subscribed' : 'is-subscribed'}`}
            />

            {
                needsRenew &&
                (
                    <div className="header__subscribe">
                        <ButtonPillGradient
                            link={RoutePaths.ChoosePlan}
                            text="Subscribe"
                            className=""
                            background="linear-gradient(225deg, rgba(187, 54, 184, 1) 0%, #b760b4 100%)"
                            backgroundHover="#b760b4"
                            color=""
                        />
                    </div>
                )
            }

            {
                doesAuthTokenExist() &&
                (
                    CanAccessMemberOnlyContent(
                        userData?.subscription?.status,
                        userData?.subscription?.plan_id,
                        userData?.admin_role_id,
                        userData?.subscription?.is_entitled
                    )
                ) && (
                    <>
                        <Notifications/>

                        <div className="header__changelog"
                             onClick={() => openPopup(PopupType.CHANGELOG)}
                        >
                            {IconInfoCircle()}
                        </div>

                        <ChangelogPopup/>
                    </>
                )
            }

            <AccountDropDown
                authUser={userData}
                logOut={logOutUser}
                tokenExists={doesAuthTokenExist()}
            />
        </header>
    );
};

export default Header;
